import { Injectable, inject } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import {
    ApiFragment,
    ApiQuery,
    ApiThesaurusConcept,
    DottedKey,
    FieldDefinition,
    FormSelectOption,
    Identifier,
    Metadata,
    MultiFormSelectOptions,
    Paginated,
    PaginatedResults,
    QueryPart,
    QueryPartStrategy,
    RecordSearchRequest,
    ShAbstractHttpService,
    ShFieldDefinitionService,
    ShPaginationService,
    Thesaurus,
    ThesaurusConceptReference,
    ThesaurusScheme,
    ZoneRestrictions,
    apiThesaurusConceptToReference,
    isNotNullOrUndefined,
    multiFormSelectOptionWrapper
} from '@stylehaven/components';
import { GetApiThesaurusConceptLabel } from '../../../thesaurus/helpers/api-thesaurus-concept-label.helper';
import { ExtractThesaurusId } from '../../../thesaurus/helpers/extract-thesaurus-id.helper';
import { GetThesaurusConceptReferenceLabel } from '../../../thesaurus/helpers/thesaurus-concept-reference-label.helper';
import { ThesaurusSuggestion } from '../../entities/mediahaven-api/thesaurus-suggestion.entity';

@Injectable({ providedIn: 'root' })
export class ThesaurusService {
    // Injectors
    private readonly paginationService: ShPaginationService = inject(ShPaginationService);

    constructor(
        private httpService: ShAbstractHttpService,
        private readonly translateService: SharedLanguageService,
        private fieldDefinitionService: ShFieldDefinitionService
    ) {}

    private readonly suggestionsEndpoint: string = `v2/thesaurus-suggestions`;
    private readonly thesaurusEndpoint: string = 'v2/thesauri';

    public getThesaurusFieldResults(
        thesaurusDefDottedKey: DottedKey,
        searchTerm: string,
        nrOfResults: number = 100
    ): Observable<ThesaurusConceptReference[]> {
        return this.fieldDefinitionService
            .getThesaurusFieldConcepts(
                thesaurusDefDottedKey,
                searchTerm,
                this.translateService.getCurrentLanguage(),
                nrOfResults
            )
            .pipe(map(concepts => concepts.map(concept => apiThesaurusConceptToReference(concept))));
    }

    public getThesaurusUriFieldResults(
        thesaurusUriFieldDefinition: FieldDefinition,
        searchTerm?: string,
        nrOfResults: number = 100
    ): Observable<MultiFormSelectOptions<string>> {
        if (!thesaurusUriFieldDefinition.ParentDottedKey) {
            console.error(
                `ThesaurusUriField ${thesaurusUriFieldDefinition.DottedKey} does not have parent ThesaurusField.`
            );
            return of(multiFormSelectOptionWrapper<string>([]));
        }

        const thesaurusFieldDefinition$: Observable<FieldDefinition> = this.fieldDefinitionService
            .getByDottedKey(thesaurusUriFieldDefinition.ParentDottedKey)
            .pipe(isNotNullOrUndefined());

        return thesaurusFieldDefinition$.pipe(
            switchMap(thesaurusField =>
                this.fieldDefinitionService.getThesaurusFieldConcepts(
                    thesaurusField.DottedKey,
                    searchTerm ?? '',
                    this.translateService.getCurrentLanguage(),
                    nrOfResults
                )
            ),
            map(concepts => {
                const options: FormSelectOption<string>[] = concepts.map(concept =>
                    this.thesaurusConceptToFormSelectOption(concept)
                );
                return multiFormSelectOptionWrapper(options);
            })
        );
    }

    public getThesaurusConceptById(
        thesaurusDefDottedKey: DottedKey,
        thesaurusConceptId: string
    ): Observable<ApiThesaurusConcept> {
        return this.fieldDefinitionService.getThesaurusFieldConcept(thesaurusDefDottedKey, thesaurusConceptId);
    }

    public getThesaurusConceptByUri(
        thesaurusDefDottedKey: DottedKey,
        thesaurusConceptUri: string
    ): Observable<ApiThesaurusConcept> {
        const conceptId: string | undefined = ExtractThesaurusId(thesaurusConceptUri);

        if (!conceptId) {
            const message = `ConceptId could not be extracted from ConceptUri ${thesaurusConceptUri}`;
            console.error(message);
            throw new Error(message);
        }

        return this.getThesaurusConceptById(thesaurusDefDottedKey, conceptId);
    }

    public getThesaurusFieldNarrowerConcepts(
        thesaurusDefDottedKey: DottedKey,
        thesaurusConceptId: string,
        startIndex: number = 0
    ): Observable<PaginatedResults<ApiThesaurusConcept>> {
        return this.fieldDefinitionService.getThesaurusFieldNarrowerConcepts(
            thesaurusDefDottedKey,
            thesaurusConceptId,
            startIndex
        );
    }

    public thesaurusConceptToFormSelectOption(concept: ApiThesaurusConcept): FormSelectOption<string> {
        return {
            label: this.getApiThesaurusConceptLabel(concept),
            value: concept.Uri
        };
    }

    public getApiThesaurusConceptLabel(concept: ApiThesaurusConcept): string {
        return (
            GetApiThesaurusConceptLabel(concept, this.translateService.getCurrentLanguage()) ??
            this.getNoTranslationLabel(concept.Uri)
        );
    }

    public getThesaurusConceptReferenceLabel(conceptRef: ThesaurusConceptReference): string {
        return (
            GetThesaurusConceptReferenceLabel(conceptRef, this.translateService.getCurrentLanguage()) ??
            this.getNoTranslationLabel(conceptRef.Uri)
        );
    }

    private getNoTranslationLabel(conceptUri: string): string {
        const conceptId: string | undefined = ExtractThesaurusId(conceptUri);

        console.error(`No translation found for ${this.translateService.getCurrentLanguage()} for uri: ${conceptUri}`);
        return this.translateService.instant('thesaurus.error.no-translation', { id: conceptId });
    }

    public submitThesaurusSuggestion(
        thesaurusDefDottedKey: string,
        recordIds: Identifier[],
        suggestion: string
    ): Observable<ApiFragment> {
        const request = `${this.suggestionsEndpoint}`;

        const body: ThesaurusSuggestion = {
            Label: suggestion,
            DottedKey: thesaurusDefDottedKey,
            Records: recordIds
        };

        return this.httpService.post<ApiFragment>(request, body);
    }

    public searchSuggestions(thesaurusDefDottedKey: string, suggestions: string[]): Observable<ApiFragment[]> {
        const recordRequest: RecordSearchRequest = new RecordSearchRequest(
            new ApiQuery(
                [
                    new QueryPart({
                        field: 'Structural.FieldDefinition.DottedKey',
                        strategy: QueryPartStrategy.MatchAll,
                        values: [thesaurusDefDottedKey]
                    }),
                    new QueryPart({
                        field: 'Descriptive.Title',
                        strategy: QueryPartStrategy.MatchAtLeastOne,
                        values: suggestions
                    })
                ],
                ZoneRestrictions.fullZoneSearch()
            )
        );

        return this.paginationService
            .getAll<Metadata>(this.suggestionsEndpoint, recordRequest.toHttpParams())
            .pipe(map(suggestions => suggestions.map(suggestion => new ApiFragment(suggestion))));
    }

    public getThesauri(): Observable<Paginated<Thesaurus>> {
        return this.httpService.get(this.thesaurusEndpoint);
    }

    public getThesaurusById(thesaurusId: Identifier): Observable<Thesaurus> {
        return this.httpService.get(`${this.thesaurusEndpoint}/${thesaurusId}`);
    }

    public getThesaurusSchemes(thesaurusId: Identifier): Observable<Paginated<ThesaurusScheme>> {
        return this.httpService.get(`${this.thesaurusEndpoint}/${thesaurusId}/schemes`);
    }
}
